import { Box, Button, Text } from '@betty/moonlight';
import { memo, useCallback, useState } from 'react';
import { CancelWithdrawalProps } from './CancelWithdrawal.types';
import { StackNavigationProp, useNavigation } from '@betty/navigation';
import { logError } from '@betty/toolkit/logging';
import { useDispatch } from '@betty/toolkit/state';
import { resetTransactionsLastTimestamp } from '../../state';
import { WithdrawService } from '@betty/wallet';

function CancelWithdrawal({
  close,
  amount,
  transactionId,
  paymentMethodInfo,
}: CancelWithdrawalProps) {
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const handleCancel = useCallback(async () => {
    try {
      setLoading(true);

      await WithdrawService.cancel({
        pathParams: { transactionId: transactionId.toString() },
      });

      dispatch(resetTransactionsLastTimestamp());

      navigation.navigate('PaymentStatusScreen', {
        transactionId: `${transactionId}`,
      });
    } catch (error) {
      logError('Withdraw cancel request failure.', error);
    } finally {
      setLoading(false);
      close();
    }
  }, [close, dispatch, navigation, transactionId]);

  return (
    <Box alignItems="center" gap="space-600">
      <Box paddingVertical="space-600" width="100%" gap="space-400">
        <Text
          variant="body-m"
          color="darkSurfacePrimary"
          locNS="account"
          locKey="cancelWithdrawalMessage"
        />
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          borderColor="borderSecondary"
          borderWidth="border-width-050"
          padding="space-400"
          borderRadius="radius-200"
        >
          <Box>
            <Text
              locNS="account"
              locKey="cancelWithdrawalText"
              variant="body-m"
              color="darkSurfacePrimary"
            />
            {paymentMethodInfo ? (
              <Text variant="body-s" color="darkSurfaceMuted">
                {paymentMethodInfo}
              </Text>
            ) : null}
          </Box>
          <Text variant="h5" color="darkSurfacePrimary">
            {amount}
          </Text>
        </Box>
      </Box>
      <Box width="100%" flexDirection="row" gap="space-200">
        <Button
          flex={1}
          variant="secondary"
          locNS="account"
          locKey="cancelWithdrawalClose"
          onPress={close}
        />
        <Button
          flex={1}
          variant="primary"
          locNS="account"
          locKey="cancelWithdrawalCta"
          onPress={handleCancel}
          loading={loading}
        />
      </Box>
    </Box>
  );
}

export default memo(CancelWithdrawal);
